import React, { useEffect, useState } from 'react';
import PecheCaptureQuestionsForm from './peche-capture-questions-form.js';
import ChasseCaptureQuestionsForm from './chasse-capture-questions-form.js';

const PECHE_NUMERIC_FIELDS = [
    'completQuantite', 'completMasse',
    'eviscereQuantite', 'eviscereMasse',
    'eteteQuantite', 'eteteMasse',
    'consommeQuantite', 'nonPeseeQuantite', 'remisAEau',
    'longueur',
];

const CHASSE_NUMERIC_FIELDS = [
    'nombreCaptureRapportee',
    'nombreCaptureConsommee',
];

const PECHE_TOGGLE_CLEARS = {
    aConsommeEspece: { whenNotTrue: { consommeQuantite: '' } },
    aRemisAEau: { whenNotTrue: { remisAEau: '' } },
    aPese: {
        whenNotTrue: {
            eviscereQuantite: '',
            eviscereMasse: '',
            eteteQuantite: '',
            eteteMasse: '',
            completQuantite: '',
            completMasse: '',
        },
    },
    aNonPese: { whenNotTrue: { nonPeseeQuantite: '' } },
};

const CHASSE_TOGGLE_CLEARS = {
    aCaptureRapportee: { whenNotTrue: { nombreCaptureRapportee: '' } },
    aCaptureConsommee: { whenNotTrue: { nombreCaptureConsommee: '', arme: '' } },
};

const getDetailId = (detail, isChasse) =>
    isChasse
        ? (detail.idGibier || detail.IdGibier || detail.id || detail.Id)
        : (detail.idEspece || detail.IdEspece || detail.id || detail.Id);

const findDetailIndex = (capturesDetails, id, isChasse) =>
    capturesDetails.findIndex(detail => String(getDetailId(detail, isChasse)) === String(id));

const buildQuestionExtraUpdates = (field, value, isChasse) => {
    const toggleClears = isChasse ? CHASSE_TOGGLE_CLEARS : PECHE_TOGGLE_CLEARS;
    const toggleClear = toggleClears[field];
    if (toggleClear && value !== true) {
        return toggleClear.whenNotTrue;
    }
    if (!isChasse) {
        if (field === 'eviscereQuantite' && !Number(value)) {
            return { eviscereMasse: '' };
        }
        if (field === 'eteteQuantite' && !Number(value)) {
            return { eteteMasse: '' };
        }
        if (field === 'completQuantite' && !Number(value)) {
            return { completMasse: '' };
        }
    }
    return null;
};

const CaptureDetailsTable = ({
    texts,
    capturesDetails,
    onDetailChange,
    mode = 'peche',
    excludeSpeciesIds = [],
    resetSelectionKey = '',
    onSpeciesChange,
    lockSpeciesDropdown = false,
    lockedSpeciesId = '',
    activeSpeciesId = '',
    showInstruction = true,
}) => {
    const isChasse = mode === 'chasse';
    const numericFields = isChasse ? CHASSE_NUMERIC_FIELDS : PECHE_NUMERIC_FIELDS;
    const [selectedSpeciesId, setSelectedSpeciesId] = useState('');

    const speciesIdsKey = capturesDetails
        .map(detail => getDetailId(detail, isChasse))
        .filter(Boolean)
        .join('|');

    const availableDetails = capturesDetails.filter(detail => {
        const detailId = getDetailId(detail, isChasse);
        if (!detailId) {
            return false;
        }
        if (lockSpeciesDropdown && lockedSpeciesId && String(detailId) === String(lockedSpeciesId)) {
            return true;
        }
        return !excludeSpeciesIds.includes(String(detailId));
    });

    useEffect(() => {
        if (lockSpeciesDropdown) {
            return;
        }
        if (activeSpeciesId) {
            setSelectedSpeciesId(String(activeSpeciesId));
            return;
        }
        setSelectedSpeciesId('');
    }, [resetSelectionKey, activeSpeciesId, lockSpeciesDropdown]);

    useEffect(() => {
        if (lockSpeciesDropdown && lockedSpeciesId) {
            setSelectedSpeciesId(String(lockedSpeciesId));
        }
    }, [lockSpeciesDropdown, lockedSpeciesId]);

    useEffect(() => {
        if (lockSpeciesDropdown) {
            return;
        }
        setSelectedSpeciesId(prev => {
            if (!prev) {
                return '';
            }
            return findDetailIndex(capturesDetails, prev, isChasse) >= 0 ? prev : '';
        });
    }, [speciesIdsKey, capturesDetails, isChasse, lockSpeciesDropdown]);

    const selectedIndex = findDetailIndex(
        capturesDetails,
        lockSpeciesDropdown && lockedSpeciesId ? String(lockedSpeciesId) : selectedSpeciesId,
        isChasse
    );

    const handleSpeciesSelect = (speciesId) => {
        if (lockSpeciesDropdown) {
            return;
        }
        setSelectedSpeciesId(speciesId);
        if (onSpeciesChange) {
            onSpeciesChange(speciesId);
        }
    };

    const handleQuestionChange = (index, field, rawValue, passedExtraUpdates = null) => {
        const value = numericFields.includes(field)
            ? (rawValue === '' ? '' : Number(rawValue))
            : rawValue;
        const toggleExtraUpdates = buildQuestionExtraUpdates(field, value, isChasse);
        const extraUpdates = {
            ...(toggleExtraUpdates || {}),
            ...(passedExtraUpdates || {}),
        };
        const hasExtraUpdates = Object.keys(extraUpdates).length > 0;
        onDetailChange(index, field, value, hasExtraUpdates ? extraUpdates : null);
    };

    return (
        <div className="fec-capture-table-section">
            {showInstruction &&
                <div className="fec-capture-table-instruction">
                    <span className="droit-acces-span">
                        {texts.captureInstruction}
                    </span>
                </div>
            }
            <div className="fec-capture-list">
                {availableDetails.length === 0 ?
                    <div className="fec-capture-table-empty">
                        {texts.noEspece}
                    </div>
                    :
                    <div className="fec-capture-species-entry">
                        <select
                            className="fec-select fec-capture-species-select"
                            value={lockSpeciesDropdown && lockedSpeciesId
                                ? String(lockedSpeciesId)
                                : selectedSpeciesId}
                            disabled={lockSpeciesDropdown}
                            onChange={e => handleSpeciesSelect(e.target.value)}
                        >
                            <option value="">{texts.chooseOption}</option>
                            {availableDetails.map(detail => {
                                const detailId = getDetailId(detail, isChasse);
                                return (
                                    <option key={detailId} value={detailId}>
                                        {detail.descriptionEspece}
                                    </option>
                                );
                            })}
                        </select>

                        {selectedIndex >= 0 && !isChasse &&
                            <PecheCaptureQuestionsForm
                                texts={texts}
                                detail={capturesDetails[selectedIndex]}
                                onChange={(field, value, extraUpdates) =>
                                    handleQuestionChange(selectedIndex, field, value, extraUpdates)
                                }
                            />
                        }

                        {selectedIndex >= 0 && isChasse &&
                            <ChasseCaptureQuestionsForm
                                texts={texts}
                                detail={capturesDetails[selectedIndex]}
                                onChange={(field, value, extraUpdates) =>
                                    handleQuestionChange(selectedIndex, field, value, extraUpdates)
                                }
                            />
                        }
                    </div>
                }
            </div>
        </div>
    );
};

export default CaptureDetailsTable;
